/**
 * PendingActionCard — amber confirmation card for Tendril write capabilities.
 *
 * Renders when the agent loop returns a pendingAction. Shows the confirm
 * template text and two buttons: Confirm and Cancel.
 *
 * Accessibility:
 *   - role="alert" — screen readers announce the card when it appears
 *   - Confirm and Cancel buttons are clearly labeled
 *   - focus-visible outlines in CSS
 *
 * BEM class: .pending-action-card
 */
import React from 'react';
import '../../styles/components/tendril.css';

/**
 * Substitute `{key}` placeholders in a template string with values from args.
 * Unrecognised placeholders are left as-is.
 *
 * @param {string} template - e.g. "Enroll you in {workshop} on {date}?"
 * @param {Record<string, string | number>} args - e.g. { workshop: "AI Fundamentals", date: "July 10" }
 * @returns {string}
 */
function interpolateTemplate(template, args) {
  if (!template || !args) return template;
  return template.replace(/\{(\w+)\}/g, (match, key) =>
    Object.prototype.hasOwnProperty.call(args, key) ? String(args[key]) : match
  );
}

/** "liveDate" -> "Live date" */
export function humanizeField(field) {
  const spaced = String(field).replace(/([a-z0-9])([A-Z])/g, '$1 $2').toLowerCase();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

function formatValue(v) {
  if (v === null || v === undefined || v === '') return '(empty)';
  return typeof v === 'object' ? JSON.stringify(v) : String(v);
}

/**
 * @param {{
 *   pendingAction: { capabilityName: string, confirmTemplate: string, args: object },
 *   onConfirm: () => void,
 *   onCancel: () => void,
 *   loading?: boolean,
 * }} props
 */
export default function PendingActionCard({ pendingAction, onConfirm, onCancel, loading = false }) {
  if (!pendingAction) return null;

  // Prefer the server-rendered text (resolved against validated entities);
  // fall back to client-side interpolation for older pending actions.
  const displayText =
    pendingAction.confirmText ??
    interpolateTemplate(pendingAction.confirmTemplate, pendingAction.args ?? {});
  const diff = Array.isArray(pendingAction.diff) ? pendingAction.diff : [];

  return (
    <div className="pending-action-card" role="alert">
      <p className="pending-action-card__label">Confirmation required</p>
      <p className="pending-action-card__template">
        {displayText}
      </p>
      {diff.length > 0 && (
        <ul className="pending-action-card__diff" aria-label="Changes">
          {diff.map((d) => (
            <li key={d.field}>
              <strong>{humanizeField(d.field)}</strong>: {formatValue(d.before)} {'\u2192'} {formatValue(d.after)}
            </li>
          ))}
        </ul>
      )}
      <div className="pending-action-card__actions">
        <button
          className="pending-action-card__confirm-btn"
          onClick={onConfirm}
          disabled={loading}
          aria-label="Confirm action"
        >
          Confirm
        </button>
        <button
          className="pending-action-card__cancel-btn"
          onClick={onCancel}
          disabled={loading}
          aria-label="Cancel action"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
